/**
 * Pure helpers for the "CTA links" (internal linking) section — shared by the category form
 * today, reusable by the corner and retailer forms later (#2378).
 *
 * A CTA link is: { openInNewTab: bool, translations: { [locale]: { text, url } } }
 * The array order is the display order (no explicit position field).
 *
 * Validation helpers accept an optional `localeCodes` list (the organization locales): when
 * provided, only those locales are considered — a stored translation for a locale removed from
 * the organization must not block the submit invisibly. Emptiness/stripping still looks at ALL
 * translations so a row carrying hidden-locale data is never silently dropped.
 */

const CTA_URL_PATTERN = /^(https?:\/\/|\/(?!\/))/;

function translationEntries(ctaLink, localeCodes = null) {
    const translations = ctaLink.translations ?? {};

    if (localeCodes === null) {
        return Object.values(translations);
    }

    return localeCodes.map((localeCode) => translations[localeCode] ?? {});
}

let nextCtaLinkClientId = 0;

/**
 * Client-side only identifier, used as a stable React key across reorders (CtaLinkRow holds
 * local state, so index keys would attach it to the wrong row after a move). Never sent to
 * the API: trimCtaLink rebuilds the submitted rows from the known fields only.
 */
function createCtaLinkClientId() {
    nextCtaLinkClientId += 1;

    return `cta-link-${nextCtaLinkClientId}`;
}

/** Assigns a clientId to the rows missing one (rows loaded from the API) — idempotent. */
export function withCtaLinkClientIds(ctaLinks) {
    return (ctaLinks ?? []).map((ctaLink) => (
        ctaLink.clientId ? ctaLink : { ...ctaLink, clientId: createCtaLinkClientId() }
    ));
}

export function createEmptyCtaLink(localeCodes) {
    const translations = {};
    localeCodes.forEach((localeCode) => {
        translations[localeCode] = { text: '', url: '' };
    });

    return { clientId: createCtaLinkClientId(), openInNewTab: false, translations };
}

export function isBlank(value) {
    return String(value ?? '').trim() === '';
}

/** A row where the admin typed nothing at all (any locale) — silently stripped before submit. */
export function isCtaLinkEmpty(ctaLink) {
    return translationEntries(ctaLink).every(
        (translation) => isBlank(translation?.text) && isBlank(translation?.url),
    );
}

export function stripEmptyCtaLinks(ctaLinks) {
    return (ctaLinks ?? []).filter((ctaLink) => !isCtaLinkEmpty(ctaLink));
}

/**
 * Absolute (http/https) or site-relative (/...) — same minimal rule as the API constraint.
 * Protocol-relative urls (//evil.com/...) are rejected on both sides.
 */
export function isValidCtaUrl(url) {
    return CTA_URL_PATTERN.test(String(url ?? '').trim());
}

/** The API requires at least one locale with both text and url filled. */
export function hasCompleteTranslation(ctaLink, localeCodes = null) {
    return translationEntries(ctaLink, localeCodes).some(
        (translation) => !isBlank(translation?.text) && !isBlank(translation?.url) && isValidCtaUrl(translation.url),
    );
}

/** A non-empty row that would fail the API validation: no locale with both text and url. */
export function isCtaLinkIncomplete(ctaLink, localeCodes = null) {
    return !isCtaLinkEmpty(ctaLink) && !hasCompleteTranslation(ctaLink, localeCodes);
}

/**
 * No organization locale holds any content — on a non-empty row, it means everything sits
 * in locales removed from the organization, invisible and not editable in the form.
 */
export function hasOnlyHiddenLocaleContent(ctaLink, localeCodes) {
    return translationEntries(ctaLink, localeCodes).every(
        (translation) => isBlank(translation?.text) && isBlank(translation?.url),
    );
}

export function hasOnlyValidUrls(ctaLink, localeCodes = null) {
    return translationEntries(ctaLink, localeCodes).every(
        (translation) => isBlank(translation?.url) || isValidCtaUrl(translation.url),
    );
}

/** True when every non-empty row would pass the API validation (org locales only). */
export function areCtaLinksSubmittable(ctaLinks, localeCodes = null) {
    return stripEmptyCtaLinks(ctaLinks).every(
        (ctaLink) => hasCompleteTranslation(ctaLink, localeCodes) && hasOnlyValidUrls(ctaLink, localeCodes),
    );
}

export function moveCtaLink(ctaLinks, index, offset) {
    const target = index + offset;
    if (target < 0 || target >= ctaLinks.length) {
        return ctaLinks;
    }

    const reordered = [...ctaLinks];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    return reordered;
}

/**
 * Values are validated trimmed, so they must be SENT trimmed too — otherwise a pasted
 * " /b/asics" would pass the front validation and get rejected by the API constraint.
 */
export function trimCtaLink(ctaLink) {
    const translations = {};
    Object.entries(ctaLink.translations ?? {}).forEach(([localeCode, translation]) => {
        translations[localeCode] = {
            text: String(translation?.text ?? '').trim(),
            url: String(translation?.url ?? '').trim(),
        };
    });

    return { openInNewTab: !!ctaLink.openInNewTab, translations };
}

/**
 * The value sent to the API: null must stay null when the feature was never used on this
 * category (the Marketplace then omits the field from the Sylius push entirely), while an
 * emptied list is sent as [] so Sylius erases the previous value.
 */
export function ctaLinksSubmitValue(ctaLinks, initialCtaLinks) {
    const stripped = stripEmptyCtaLinks((ctaLinks ?? []).map(trimCtaLink));

    if (stripped.length === 0 && (initialCtaLinks === null || initialCtaLinks === undefined)) {
        return null;
    }

    return stripped;
}
